import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Download, Plus, Trash2, BellRing, Send, ShieldCheck, ShieldOff, UserCog, Ban, UserCheck, History, RefreshCw, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errText, formatPrice, BACKEND_URL } from "@/lib/api";
import EnquiryStatusSelect, { STATUSES } from "@/components/EnquiryStatusSelect";
import EnquiryAssignSelect from "@/components/EnquiryAssignSelect";
import DealerScorecard from "@/components/DealerScorecard";
import BannersTab from "@/components/BannersTab";
import ActivityLogTab from "@/components/ActivityLogTab";
import CampaignsTab from "@/components/CampaignsTab";
import SavedViews from "@/components/SavedViews";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [props, setProps] = useState([]);
  const [categories, setCategories] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [users, setUsers] = useState([]);
  const [team, setTeam] = useState([]);
  const [assigneeFilter, setAssigneeFilter] = useState("all");
  const [range, setRange] = useState({ from: "", to: "" });
  const [dealerFilter, setDealerFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [views, setViews] = useState([]);
  const [board, setBoard] = useState(null);
  const [tab, setTab] = useState("enquiries");
  const [refreshing, setRefreshing] = useState(false);
  const [newCat, setNewCat] = useState({ name: "", description: "" });

  const load = useCallback(async () => {
    try {
      const [s, e, p, c, a, u, t, sv, sb] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/enquiries"),
        api.get("/admin/properties"),
        api.get("/categories"),
        api.get("/alerts"),
        api.get("/admin/users"),
        api.get("/team"),
        api.get("/saved-views"),
        api.get("/admin/dealer-scorecard"),
      ]);
      setStats(s.data);
      setEnquiries(e.data);
      setProps(p.data);
      setCategories(c.data);
      setAlerts(a.data);
      setUsers(u.data);
      setTeam(t.data);
      setViews(sv.data);
      setBoard(sb.data);
    } catch (err) {
      toast.error(errText(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
    toast.success("Dashboard refreshed");
  };

  const exportUrl = (visitedOnly) => {
    const params = new URLSearchParams();
    if (visitedOnly) params.set("only_visited", "true");
    if (range.from) params.set("date_from", range.from);
    if (range.to) params.set("date_to", range.to);
    if (dealerFilter !== "all") params.set("dealer", dealerFilter);
    if (search.trim()) params.set("q", search.trim());
    const qs = params.toString();
    return `${BACKEND_URL}/api/enquiries/export${qs ? `?${qs}` : ""}`;
  };

  const dealers = useMemo(
    () => [...new Set(enquiries.map((e) => e.dealer_name).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [enquiries]
  );

  const dateFiltered = useMemo(() => {
    if (!range.from && !range.to) return enquiries;
    return enquiries.filter((e) => {
      const day = (e.created_at || "").slice(0, 10);
      if (range.from && day < range.from) return false;
      if (range.to && day > range.to) return false;
      return true;
    });
  }, [enquiries, range]);

  const addCategory = async (e) => {
    e.preventDefault();
    try {
      await api.post("/categories", newCat);
      toast.success("Category added");
      setNewCat({ name: "", description: "" });
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const delCategory = async (slug) => {
    try {
      await api.delete(`/categories/${slug}`);
      toast.success("Category removed");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const delProperty = async (id) => {
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Listing removed");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const notifyMatches = async (id) => {
    try {
      const { data } = await api.post(`/properties/${id}/notify-matches`);
      if (data.notified) {
        toast.success(
          `Alert emails sent to ${data.notified} subscriber(s)` +
            (data.skipped ? ` · ${data.skipped} already got this one` : "")
        );
      } else if (data.skipped) {
        toast.info(`All ${data.skipped} matching subscriber(s) were already emailed about this property`);
      } else {
        toast.success("No subscribers match this property");
      }
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const setStayAccess = async (userId, granted, scope = "stay") => {
    try {
      await api.post(`/admin/users/${userId}/stay-access`, { granted, scope });
      const label = scope === "rental" ? "Rentals" : scope === "both" ? "PG + rentals" : "Stay management";
      toast.success(granted ? `${label} access granted` : `${label} access revoked`);
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const setRole = async (userId, role) => {
    if (!window.confirm(role === "admin" ? "Make this user an admin?" : "Remove admin rights from this user?")) return;
    try {
      await api.post(`/admin/users/${userId}/role`, { role });
      toast.success(role === "admin" ? "Admin rights granted" : "Admin rights removed");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const setBlocked = async (userId, blocked) => {
    try {
      await api.post(`/admin/users/${userId}/block`, { blocked });
      toast.success(blocked ? "User blocked" : "User unblocked");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}? Their listings, PGs and saved items will be removed.`)) return;
    try {
      await api.delete(`/admin/users/${u.user_id}`);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const delAlert = async (id) => {
    try {
      await api.delete(`/alerts/${id}`);
      toast.success("Alert removed");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const visibleEnquiries = useMemo(() => {
    let rows = dateFiltered;
    if (dealerFilter !== "all") rows = rows.filter((e) => (e.dealer_name || "") === dealerFilter);
    if (assigneeFilter === "none") rows = rows.filter((e) => !e.assigned_to);
    else if (assigneeFilter !== "all") rows = rows.filter((e) => e.assigned_to === assigneeFilter);
    const needle = search.trim().toLowerCase();
    if (needle) {
      rows = rows.filter((e) =>
        [e.name, e.phone, e.email, e.property_title, e.property_city, e.dealer_name, e.dealer_phone, e.message, e.assigned_to_name]
          .join(" ")
          .toLowerCase()
          .includes(needle)
      );
    }
    return rows;
  }, [dateFiltered, assigneeFilter, dealerFilter, search]);

  return (
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Admin dashboard</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">Listings, enquiries and categories at a glance</p>
        </div>
        <Button
          variant="outline"
          onClick={refresh}
          disabled={refreshing}
          className="h-11 rounded-lg font-semibold"
          data-testid="admin-refresh-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </Button>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="admin-stats">
        {[
          { label: "Properties", value: stats?.properties ?? "—" },
          { label: "Enquiries", value: stats?.enquiries ?? "—" },
          { label: "Site visits", value: stats?.site_visits ?? "—" },
          { label: "Registered users", value: stats?.users ?? "—" },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl border border-border p-6">
            <p className="font-display text-3xl">{s.value}</p>
            <p className="mt-1 text-[13px] text-muted-foreground">{s.label}</p>
          </div>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-10">
        <TabsList className="w-full justify-start overflow-x-auto no-scrollbar rounded-full bg-muted p-1">
          <TabsTrigger value="enquiries" className="rounded-full px-5" data-testid="admin-tab-enquiries">Enquiries</TabsTrigger>
          <TabsTrigger value="properties" className="rounded-full px-5" data-testid="admin-tab-properties">Properties</TabsTrigger>
          <TabsTrigger value="categories" className="rounded-full px-5" data-testid="admin-tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="alerts" className="rounded-full px-5" data-testid="admin-tab-alerts">Alerts</TabsTrigger>
          <TabsTrigger value="activity" className="rounded-full px-5" data-testid="admin-tab-activity">Activity log</TabsTrigger>
          <TabsTrigger value="dealers" className="rounded-full px-5" data-testid="admin-tab-dealers">Dealers</TabsTrigger>
          <TabsTrigger value="banners" className="rounded-full px-5" data-testid="admin-tab-banners">Banners</TabsTrigger>
          <TabsTrigger value="campaigns" className="rounded-full px-5" data-testid="admin-tab-campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="access" className="rounded-full px-5" data-testid="admin-tab-access">
            Users &amp; access{stats?.pending_stay_requests ? ` (${stats.pending_stay_requests})` : ""}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="enquiries" className="mt-7">
          <SavedViews
            views={views}
            filters={{
              search,
              dealer: dealerFilter,
              date_from: range.from,
              date_to: range.to,
              assignee: assigneeFilter,
            }}
            onApply={(view) => {
              const f = view.filters || {};
              setSearch(f.search || "");
              setDealerFilter(f.dealer || "all");
              setAssigneeFilter(f.assignee || "all");
              setRange({ from: f.date_from || "", to: f.date_to || "" });
              toast.success(`Showing "${view.name}"`);
            }}
            onChanged={load}
          />
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-[13px] text-muted-foreground">
              {enquiries.length} enquiries · {enquiries.filter((e) => !e.assigned_to).length} unassigned ·{" "}
              {enquiries.filter((e) => e.visited).length} site visits ·{" "}
              {STATUSES.map((s) => `${enquiries.filter((e) => (e.status || "new") === s.value).length} ${s.label.toLowerCase()}`).join(" · ")}
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                <SelectTrigger className="h-11 w-[210px] rounded-lg text-[13px] font-semibold" data-testid="admin-assignee-filter">
                  <SelectValue placeholder="All assignees" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="assignee-filter-all">All enquiries</SelectItem>
                  <SelectItem value="none" data-testid="assignee-filter-none">Unassigned only</SelectItem>
                  {team.map((m) => (
                    <SelectItem key={m.user_id} value={m.user_id} data-testid={`assignee-filter-${m.user_id}`}>
                      {m.name || m.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <a href={exportUrl(true)} data-testid="admin-export-visited-excel-button">
                <Button variant="outline" className="h-11 rounded-lg font-semibold">
                  <Download className="h-4 w-4 mr-2" /> Visitors only
                </Button>
              </a>
              <a href={exportUrl(false)} data-testid="admin-export-excel-button">
                <Button className="h-11 rounded-lg font-bold hover:brightness-95">
                  <Download className="h-4 w-4 mr-2" /> Download Excel
                </Button>
              </a>
            </div>
          </div>

          <div className="mb-5 flex flex-wrap items-end gap-3 rounded-2xl border border-border p-4">
            <div className="space-y-1.5">
              <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">Search</Label>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, phone, email or property"
                className="h-11 w-[240px] rounded-lg"
                data-testid="admin-enquiry-search"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">Dealer</Label>
              <Select value={dealerFilter} onValueChange={setDealerFilter}>
                <SelectTrigger className="h-11 w-[210px] rounded-lg text-[13px] font-semibold" data-testid="admin-dealer-filter">
                  <SelectValue placeholder="All dealers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="dealer-filter-all">All dealers</SelectItem>
                  {dealers.map((d) => (
                    <SelectItem key={d} value={d} data-testid={`dealer-filter-${d}`}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">From date</Label>
              <Input
                type="date"
                value={range.from}
                max={range.to || undefined}
                onChange={(e) => setRange({ ...range, from: e.target.value })}
                className="h-11 w-[170px] rounded-lg"
                data-testid="admin-export-from-date"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground">To date</Label>
              <Input
                type="date"
                value={range.to}
                min={range.from || undefined}
                onChange={(e) => setRange({ ...range, to: e.target.value })}
                className="h-11 w-[170px] rounded-lg"
                data-testid="admin-export-to-date"
              />
            </div>
            {(range.from || range.to || dealerFilter !== "all" || search) && (
              <Button
                variant="ghost"
                className="h-11 rounded-lg font-semibold"
                onClick={() => {
                  setRange({ from: "", to: "" });
                  setDealerFilter("all");
                  setSearch("");
                }}
                data-testid="admin-export-clear-dates"
              >
                Clear filters
              </Button>
            )}
            <p className="ml-auto text-[12px] text-muted-foreground" data-testid="admin-export-range-hint">
              Showing {visibleEnquiries.length} of {enquiries.length} enquiries
              {dealerFilter !== "all" ? ` · dealer ${dealerFilter}` : ""}
              {range.from || range.to
                ? ` · ${range.from || "start"} to ${range.to || "today"}`
                : ""}
              {" · Excel downloads exactly what you see here"}
            </p>
          </div>
          <div className="rounded-2xl border border-border overflow-x-auto">
            <Table className="min-w-[900px]" data-testid="admin-enquiries-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>Dealer / owner</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Visited</TableHead>
                  <TableHead>Visit date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Following up</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleEnquiries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} className="text-sm text-muted-foreground">No enquiries to show.</TableCell>
                  </TableRow>
                ) : (
                  visibleEnquiries.map((e) => (
                    <TableRow key={e.enquiry_id} data-testid={`admin-enquiry-row-${e.enquiry_id}`}>
                      <TableCell className="text-xs">{(e.created_at || "").slice(0, 10)}</TableCell>
                      <TableCell className="text-sm font-semibold">{e.name}</TableCell>
                      <TableCell className="text-sm">{e.phone}</TableCell>
                      <TableCell className="text-sm">{e.email || "—"}</TableCell>
                      <TableCell className="text-sm">{e.property_title || "General enquiry"}</TableCell>
                      <TableCell className="text-sm" data-testid={`admin-enquiry-dealer-${e.enquiry_id}`}>
                        {e.dealer_name ? (
                          <>
                            <span className="block font-semibold">{e.dealer_name}</span>
                            {e.dealer_phone && (
                              <a
                                href={`tel:${e.dealer_phone}`}
                                className="block text-[12px] text-muted-foreground transition-colors duration-200 hover:text-primary"
                              >
                                {e.dealer_phone}
                              </a>
                            )}
                          </>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{e.property_city || "—"}</TableCell>
                      <TableCell className="text-sm">{e.visited ? "Yes" : "No"}</TableCell>
                      <TableCell className="text-sm">{e.visit_date || "—"}</TableCell>
                      <TableCell>
                        <EnquiryStatusSelect
                          enquiry={e}
                          onUpdated={(u) => setEnquiries((prev) => prev.map((x) => (x.enquiry_id === u.enquiry_id ? u : x)))}
                        />
                      </TableCell>
                      <TableCell>
                        <EnquiryAssignSelect
                          enquiry={e}
                          team={team}
                          onUpdated={(u) => {
                            setEnquiries((prev) => prev.map((x) => (x.enquiry_id === u.enquiry_id ? u : x)));
                            load();
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="dealers" className="mt-7">
          <DealerScorecard
            board={board}
            onPickDealer={(name) => {
              setDealerFilter(name);
              setTab("enquiries");
            }}
          />
        </TabsContent>

        <TabsContent value="banners" className="mt-7">
          <BannersTab />
        </TabsContent>

        <TabsContent value="campaigns" className="mt-7">
          <CampaignsTab />
        </TabsContent>

        <TabsContent value="activity" className="mt-7">
          <ActivityLogTab />
        </TabsContent>

        <TabsContent value="properties" className="mt-7">
          <div className="rounded-2xl border border-border overflow-x-auto">
            <Table data-testid="admin-properties-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Views</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {props.map((p) => (
                  <TableRow key={p.property_id}>
                    <TableCell className="text-sm font-semibold">
                      <Link to={`/property/${p.property_id}`} className="hover:underline">{p.title}</Link>
                    </TableCell>
                    <TableCell className="text-sm capitalize">{p.category}</TableCell>
                    <TableCell className="text-sm">{p.city}</TableCell>
                    <TableCell className="text-sm">{formatPrice(p.price, p.listing_type)}</TableCell>
                    <TableCell className="text-sm">{p.owner_name}</TableCell>
                    <TableCell className="text-sm">{p.views || 0}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-lg"
                          onClick={() => notifyMatches(p.property_id)}
                          data-testid={`admin-notify-matches-${p.property_id}`}
                          title="Email matching alert subscribers"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-lg text-destructive"
                          onClick={() => delProperty(p.property_id)}
                          data-testid={`admin-delete-property-${p.property_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="categories" className="mt-7 grid gap-8 lg:grid-cols-[1fr_340px]">
          <div className="rounded-2xl border border-border overflow-x-auto">
            <Table data-testid="admin-categories-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Listings</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.map((c) => (
                  <TableRow key={c.slug}>
                    <TableCell className="text-sm font-semibold">{c.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{c.slug}</TableCell>
                    <TableCell className="text-sm">{c.count}</TableCell>
                    <TableCell>
                      {!c.is_default && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-lg text-destructive"
                          onClick={() => delCategory(c.slug)}
                          data-testid={`admin-delete-category-${c.slug}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <form onSubmit={addCategory} className="h-fit rounded-2xl border border-border p-6 space-y-4" data-testid="admin-add-category-form">
            <h2 className="font-display text-lg">Custom category</h2>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Name</Label>
              <Input
                value={newCat.name}
                onChange={(e) => setNewCat({ ...newCat, name: e.target.value })}
                placeholder="Farmhouse"
                className="h-12 rounded-lg"
                data-testid="admin-category-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Description</Label>
              <Input
                value={newCat.description}
                onChange={(e) => setNewCat({ ...newCat, description: e.target.value })}
                className="h-12 rounded-lg"
                data-testid="admin-category-description-input"
              />
            </div>
            <Button type="submit" className="w-full h-12 rounded-lg font-bold hover:brightness-95" data-testid="admin-add-category-btn">
              <Plus className="h-4 w-4 mr-2" /> Add category
            </Button>
          </form>
        </TabsContent>
        <TabsContent value="alerts" className="mt-7">
          <p className="mb-4 flex items-center gap-2 text-[13px] text-muted-foreground">
            <BellRing className="h-4 w-4 text-primary" /> {alerts.length} buyers are waiting for matching properties.
            New listings email them automatically; use the send icon in the Properties tab to re-notify.
          </p>
          <div className="rounded-2xl border border-border overflow-x-auto">
            <Table data-testid="admin-alerts-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>City</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Max price</TableHead>
                  <TableHead>Bedrooms</TableHead>
                  <TableHead>Emails sent</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {alerts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-sm text-muted-foreground">No alert subscribers yet.</TableCell>
                  </TableRow>
                ) : (
                  alerts.map((a) => (
                    <TableRow key={a.alert_id} data-testid={`admin-alert-row-${a.alert_id}`}>
                      <TableCell className="text-sm font-semibold">{a.email}</TableCell>
                      <TableCell className="text-sm">{a.city || "Any"}</TableCell>
                      <TableCell className="text-sm capitalize">{a.category || "Any"}</TableCell>
                      <TableCell className="text-sm">{a.max_price ? formatPrice(a.max_price) : "Any"}</TableCell>
                      <TableCell className="text-sm">{a.bedrooms ? `${a.bedrooms}+` : "Any"}</TableCell>
                      <TableCell className="text-sm">{a.notified_count || 0}</TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="rounded-lg text-destructive"
                          onClick={() => delAlert(a.alert_id)}
                          data-testid={`admin-delete-alert-${a.alert_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
        <TabsContent value="access" className="mt-7">
          <p className="mb-4 flex items-center gap-2 text-[13px] text-muted-foreground">
            <ShieldCheck className="h-4 w-4 text-primary" /> Grant admin rights or stay / PG management access, block a
            user from signing in, or delete an account. {stats?.stays ?? 0} PGs · {stats?.stay_beds ?? 0} beds so far.
          </p>
          <div className="rounded-2xl border border-border overflow-x-auto">
            <Table className="min-w-[900px]" data-testid="admin-users-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Listings</TableHead>
                  <TableHead>Stay request</TableHead>
                  <TableHead>Module access</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.user_id} data-testid={`admin-user-row-${u.user_id}`}>
                    <TableCell className="text-sm font-semibold">{u.name || "—"}</TableCell>
                    <TableCell className="text-sm">{u.email}</TableCell>
                    <TableCell className="text-sm capitalize">{u.role}</TableCell>
                    <TableCell className="text-sm">
                      {u.properties} · {u.stays} PG
                    </TableCell>
                    <TableCell className="text-sm capitalize">
                      {u.stay_request_status === "pending" ? (
                        <span className="rounded-full bg-primary/10 px-2.5 py-1 text-[12px] font-bold text-primary">
                          Pending{(u.stay_request_scopes || []).length ? `: ${(u.stay_request_scopes || []).join(" + ")}` : ""}
                        </span>
                      ) : (
                        u.stay_request_status
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {u.role === "admin" ? (
                        <span className="font-semibold text-emerald-600">Full (admin)</span>
                      ) : (
                        <span className="flex flex-wrap gap-1" data-testid={`admin-access-badges-${u.user_id}`}>
                          <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${u.stay_access ? "bg-emerald-500/10 text-emerald-700" : "bg-muted text-muted-foreground"}`}>
                            PG {u.stay_access ? "✓" : "—"}
                          </span>
                          <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${u.rental_access ? "bg-emerald-500/10 text-emerald-700" : "bg-muted text-muted-foreground"}`}>
                            Rentals {u.rental_access ? "✓" : "—"}
                          </span>
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {u.blocked ? (
                        <span className="rounded-full bg-destructive/10 px-2.5 py-1 text-[12px] font-bold text-destructive">Blocked</span>
                      ) : (
                        <span className="text-emerald-600 font-semibold">Active</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {u.role !== "admin" && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-9 rounded-lg"
                              onClick={() => setStayAccess(u.user_id, !u.stay_access, "stay")}
                              data-testid={`admin-toggle-stay-access-${u.user_id}`}
                            >
                              {u.stay_access ? <ShieldOff className="h-4 w-4" /> : <ShieldCheck className="h-4 w-4" />}
                              <span className="ml-1.5 hidden xl:inline">{u.stay_access ? "Revoke PG" : "Grant PG"}</span>
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-9 rounded-lg"
                              onClick={() => setStayAccess(u.user_id, !u.rental_access, "rental")}
                              data-testid={`admin-toggle-rental-access-${u.user_id}`}
                            >
                              {u.rental_access ? <ShieldOff className="h-4 w-4" /> : <KeyRound className="h-4 w-4" />}
                              <span className="ml-1.5 hidden xl:inline">{u.rental_access ? "Revoke rentals" : "Grant rentals"}</span>
                            </Button>
                            {!(u.stay_access && u.rental_access) && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-9 rounded-lg"
                                onClick={() => setStayAccess(u.user_id, true, "both")}
                                data-testid={`admin-grant-both-access-${u.user_id}`}
                              >
                                <ShieldCheck className="h-4 w-4" />
                                <span className="ml-1.5 hidden xl:inline">Grant both</span>
                              </Button>
                            )}
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-9 rounded-lg"
                          onClick={() => setRole(u.user_id, u.role === "admin" ? "user" : "admin")}
                          data-testid={`admin-toggle-role-${u.user_id}`}
                          title={u.role === "admin" ? "Remove admin rights" : "Make admin"}
                        >
                          <UserCog className="h-4 w-4" />
                          <span className="ml-1.5 hidden xl:inline">{u.role === "admin" ? "Remove admin" : "Make admin"}</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-9 rounded-lg ${u.blocked ? "text-emerald-600" : ""}`}
                          onClick={() => setBlocked(u.user_id, !u.blocked)}
                          data-testid={`admin-toggle-block-${u.user_id}`}
                          title={u.blocked ? "Unblock user" : "Block user"}
                        >
                          {u.blocked ? <UserCheck className="h-4 w-4" /> : <Ban className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-9 rounded-lg text-destructive"
                          onClick={() => deleteUser(u)}
                          data-testid={`admin-delete-user-${u.user_id}`}
                          title="Delete user"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
